import { useState, KeyboardEvent, useEffect, useMemo, useRef } from 'react';
import { AppBar, Toolbar, Box, TextField, Button } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  InspactOrder,
  loadRecentOrders,
  setOrder,
} from '../../redux/reducer/inspact/order_reducer';
import { useAppSelector } from '../../redux/Store';
import CustomTable from '../../ui/organisms/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import CustomTypography from '../../ui/atoms/CustomTypography';
import useWorkOrderNumberSearch from '../../hooks/useWorkOrderNumberSearch';
import Notifications from '../../ui/molecules/toast/Notifications';
import { useFindPermissions } from '../../utils/helpers/useFindPermissions';
import { useNavigate } from 'react-router';
import { resetIsGalleryPanelOpen } from '../../redux/reducer/contextIdReducer';

const menus = [
  {
    title: 'HOME',
    link: '/home',
  },
];

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const recentOrders = useAppSelector(
    (state) => state.inspactOrder.recentOrders
  );
  const { searchWorkOrder } = useWorkOrderNumberSearch();
  const [searchValue, setSearchValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const { canSearchWorkOrder } = useFindPermissions();

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<any>();
    return [
      columnHelper.accessor('orderNumber', {
        header: 'Order Number',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('vendorCode', {
        header: 'Vendor Code',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('workCode', {
        header: 'Work Code',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
    ];
  }, []);

  const handleKeyPress = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key.toLowerCase() === 'enter') {
      try {
        dispatch(resetIsGalleryPanelOpen());
        await searchWorkOrder(searchValue);
      } catch (error: any) {
        console.log('error occurred', error);
        Notifications('Unable to load the work order', 0);
      }
      setSearchValue('');
      setShowDropdown(false);
      if (searchInputRef?.current) {
        searchInputRef?.current?.blur();
      }
    }
  };

  useEffect(() => {
    dispatch(loadRecentOrders() as any);
  }, [dispatch]);

  const onClickRecentOrder = (order: InspactOrder) => {
    dispatch(setOrder(order));
    setShowDropdown(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  // Handle focus on the search input
  const handleFocus = () => {
    setShowDropdown(true);
  };

  const handleRowClick = async (rowData: any) => {
    try {
      dispatch(resetIsGalleryPanelOpen());
      dispatch(
        setOrder({
          orderNumber: rowData?.orderNumber,
          propertyLoanId: rowData?.propertyLoanId,
        })
      );
      setSearchValue('');
      setShowDropdown(false);
      navigate(
        `/submissions/${rowData?.orderNumber}/${rowData?.propertyLoanId}`
      );
    } catch (error: any) {
      console.log('error occurred', error);
    }
  };

  const handleOverlayClick = () => {
    setShowDropdown(false);
  };

  return (
    <>
      {/* Overlay when dropdown is open */}
      {showDropdown && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10,
          }}
          onClick={handleOverlayClick}
        />
      )}

      <AppBar
        position='fixed'
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: '#ffffff',
          color: '#000000',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/home')}
          >
            <Box
              component='img'
              src='/images/safeviewlogo.png'
              alt='Logo'
              sx={{
                width: { xs: '20vw', sm: '15vw', md: '200px' },
                height: 'auto',
                maxWidth: '200px',
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              {menus.map((item, index) => (
                <Button
                  key={index}
                  onClick={() => navigate(item.link)}
                  sx={{ fontWeight: 'bold', color: 'black' }}
                >
                  {item.title}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                position: 'relative',
                width: { xs: '150px', sm: '250px', md: '350px' },
              }}
            >
              <Box
                sx={{
                  borderRadius: '50px',
                  overflow: 'hidden',
                  border: '1px solid #ccc',
                  position: 'relative',
                }}
              >
                <TextField
                  inputRef={searchInputRef}
                  disabled={!canSearchWorkOrder}
                  variant='outlined'
                  placeholder='Search Work Order'
                  size='small'
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onFocus={() => setShowDropdown(true)}
                  autoComplete='off'
                  sx={{
                    backgroundColor: '#f5f5f5',
                    width: '100%',
                    borderRadius: '50px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                    },
                    '&.Mui-disabled': {
                      cursor: 'not-allowed',
                      pointerEvents: 'all !important',
                    },
                  }}
                />
              </Box>

              {/* Recent Searches UI */}
              {showDropdown && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    backgroundColor: '#fff',
                    boxShadow: '-7px 7px 15px rgba(0,0,0,.3)',
                    maxHeight: '350px',
                    width: { xs: '90vw', sm: '575px' },
                    overflowY: 'auto',
                    display: 'block',
                    zIndex: 11,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box
                    sx={{
                      background: '#0075d6',
                      width: '100%',
                      boxShadow: 'inset 0 5px 10px rgba(0,0,0,.2)',
                    }}
                  >
                    <Box sx={{ padding: '1.25em .25em' }}>
                      <Box sx={{ display: 'flex', paddingX: '2rem' }}>
                        <Box>
                          <Button
                            variant='outlined'
                            sx={{
                              border: '1px solid #53b50a',
                              color: '#53b50a',
                              borderRadius: '1.5rem',
                              borderBottomRightRadius: '0',
                              borderTopRightRadius: '0',
                              '&:hover': {
                                backgroundColor: '#fff',
                              },
                            }}
                          >
                            Work Order
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{ backgroundColor: '#333', padding: '1.25em .25em' }}
                    >
                      <CustomTypography
                        variant='subtitle2'
                        sx={{
                          marginX: '1.1rem',
                          fontWeight: 'bold',
                          color: '#fff',
                          textTransform: 'uppercase',
                        }}
                      >
                        Recently Viewed Work Orders
                      </CustomTypography>
                    </Box>
                  </Box>
                  <CustomTable
                    columns={columns}
                    data={recentOrders}
                    borderRadiusIsPresent={false}
                    onRowClick={handleRowClick}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Header;
